import { z } from "zod";

import { looseArray } from "../looseParse";

export const cryptoChainSchema = z.object({
  chain: z.string(),
  valid: z.boolean(),
});

export const cryptoAssetSchema = z.object({
  id: z.string().optional(),
  chain: cryptoChainSchema.optional(),
  name: z.string().optional(),
  asset: z.string().optional(),
  symbol: z.string().optional(),
  network: z.string().optional(),
  assetexplorer: z.string().optional(),
  // Pointer-backed Cryptowallet columns (`GetAuthorizedAssetsRow`) without
  // `omitempty`: nil values serialize as explicit `null`, which `.optional()`
  // rejects. Use `.nullish()` so one null integration identifier can't make
  // `looseArray` drop the whole otherwise-valid asset (which would hide crypto
  // assets from transfer selectors and history mappings).
  contract_address: z.string().nullish(),
  fireblocks_id: z.string().nullish(),
  token_type: z.string().nullish(),
  regex_validation: z.string().nullish(),
  cusip: z.string().nullish(),
  sweep_min: z
    .object({
      Int: z.number().optional(),
      Exp: z.number().optional(),
      Status: z.number().optional(),
      NaN: z.boolean().optional(),
      InfinityModifier: z.number().optional(),
    })
    .optional(),
  notabene_id: z.string().nullish(),
  attributes: z.record(z.string(), z.unknown()).optional(),
});

export const cryptoAddressSchema = z.object({
  asset_id: z.string(),
  address: z.string(),
  created_at: z.string(),
  migrated_at: z.string().optional(),
});

export const cryptoTransferSchema = z.object({
  id: z.string(),
  tx_hash: z.string().nullable().optional(),
  direction: z.string(),
  amount: z.string(),
  usd_value: z.string(),
  chain: z.string(),
  asset: z.string(),
  from_address: z.string(),
  to_address: z.string(),
  status: z.string(),
  created_at: z.string(),
  network_fee: z.string(),
  fees: z.string(),
});

export const travelRuleManualEntrySchema = z.object({
  vasp_name: z.string().optional(),
  vasp_website: z.string().optional(),
});

export const travelRuleInfoSchema = z.object({
  beneficiary_vasp_id: z.string().optional(),
  beneficiary_manual_entry: travelRuleManualEntrySchema.optional(),
  beneficiary_is_self_hosted: z.boolean().optional(),
  beneficiary_given_name: z.string().optional(),
  beneficiary_family_name: z.string().optional(),
  beneficiary_entity_name: z.string().optional(),
  beneficiary_geographic_address_street_name: z.string().optional(),
  beneficiary_geographic_address_post_code: z.string().optional(),
  beneficiary_geographic_address_town_name: z.string().optional(),
  beneficiary_geographic_address_country: z.string().optional(),
  beneficiary_geographic_address_building_number: z.string().optional(),
  beneficiary_country_of_residence: z.string().optional(),
  originator_family_name: z.string().optional(),
  originator_given_name: z.string().optional(),
  originator_account_number: z.string().optional(),
  originator_geographic_address_street_name: z.string().optional(),
  originator_geographic_address_post_code: z.string().optional(),
  originator_geographic_address_town_name: z.string().optional(),
  originator_geographic_address_country: z.string().optional(),
  originator_geographic_address_building_number: z.string().optional(),
  originator_country_of_residence: z.string().optional(),
  originator_date_of_birth: z.string().optional(),
  originator_place_of_birth: z.string().optional(),
  originator_customer_identification: z.string().optional(),
  originator_national_identifier: z.string().optional(),
  originator_national_identifier_type: z.string().optional(),
  originator_entity_name: z.string().optional(),
});

export const vaspSchema = z.object({
  id: z.string(),
  name: z.string(),
  website: z.string().optional(),
  emailDomain: z.string().optional(),
  did: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  address: z.string().optional(),
});

// cryptowallet tags these three `omitempty`, so Go drops them whenever they're
// zero — which `page` always is on the first page. They must stay optional or
// every search fails envelope validation and falls back to the raw payload.
export const vaspSearchResponseSchema = z.object({
  // A nil Go slice marshals as `null`, which a plain `z.array` rejects.
  vasps: looseArray(vaspSchema, "vaspSearchResponseSchema.vasps"),
  total: z.number().optional().default(0),
  page: z.number().optional().default(0),
  pages: z.number().optional().default(0),
});

export const cryptoWhitelistedAddressSchema = z.object({
  id: z.string(),
  chain: z.string(),
  asset: z.string(),
  address: z.string(),
  created_at: z.string(),
  status: z.string(),
  address_type: z.string().optional(),
  institution_name: z.string().optional(),
  travel_rule_info: travelRuleInfoSchema.optional(),
});

export const cryptoFeesSchema = z.object({
  fee: z.string(),
  network_fee: z.string(),
});
